import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Building } from "lucide-react";

import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";

import { OnboardingInfoSchema } from "utils/validationSchema";
import { TOOLTIPS } from "utils/tooltips";
import { Mixpanel } from "utils/Analytics";
import baseApi from "utils/axios-instance";
import { toast } from "sonner";
import { CurrentUserInfo } from "pages/MVP/CurrentUserInfo";
import Tooltip from "components/ui/tooltip";

interface NewPracticeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  practiceId: string;
  refreshProfileInfo: () => void;
  currentUser?: CurrentUserInfo;
}

function NewPracticeDialog({
  open,
  onOpenChange,
  practiceId,
  refreshProfileInfo,
  currentUser,
}: NewPracticeDialogProps) {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    getValues,
    clearErrors,
    trigger,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(OnboardingInfoSchema),
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const [practiceIndex, setPracticeIndex] = useState(0);
  const [providerIndex, setProviderIndex] = useState(0);
  const [loadInfo, setLoadInfo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddingPractice, setIsAddingPractice] = useState(false);
  const [practiceName, setPracticeName] = useState("");

  // Helper function to get nested error messages
  const getFieldError = (fieldPath: string): string | null => {
    const pathArray = fieldPath.split(".");
    let error: any = errors;

    for (const key of pathArray) {
      if (error?.[key]) {
        error = error[key];
      } else {
        return null;
      }
    }

    return (error?.message as string) || null;
  };

  // Handle input changes with validation clearing
  const handleInputChange = (fieldName: string, value: string) => {
    setValue(fieldName as any, value);
    clearErrors(fieldName as any);
    // Trigger validation for the specific field after a short delay
    setTimeout(() => {
      trigger(fieldName as any);
    }, 100);
  };

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      reset();
      setLoadInfo(false);
    }
  }, [open, reset]);

  useEffect(() => {
    if (open && !loadInfo) {
      setLoadInfo(false);
      setIsAddingPractice(practiceId === "OTHER");

      baseApi
        .get("/profile")
        .then((response) => {
          setValue("specialty", response.data.specialty || "");

          let temp = response.data.practices || [];
          let newPracticeIndex = 0;
          let newProviderIndex = 0;

          // Keep only the entry this dialog adds: the schema validates every practice in the
          // form, so one invalid saved practice would block submit on a field the dialog hides.
          if (practiceId === "OTHER") {
            // Adding new practice
            const newPractice = {
              name: "",
              tax_id: "",
              address: "",
              city: "",
              state: "",
              zip: "",
              providers: [
                {
                  name: "",
                  npi: "",
                },
              ],
            };
            temp = [newPractice];
            newPracticeIndex = temp.length - 1;
            newProviderIndex = 0;
          } else {
            // Adding provider to existing practice
            const practiceIdx = temp.findIndex((p: any) => p.id === practiceId);
            if (practiceIdx !== -1) {
              setPracticeName(temp[practiceIdx].name);
              temp = [
                { ...temp[practiceIdx], providers: [{ name: "", npi: "" }] },
              ];
            }
          }

          setPracticeIndex(newPracticeIndex);
          setProviderIndex(newProviderIndex);
          setValue("practices", temp);
          setLoadInfo(true);
        })
        .catch((error) => {
          console.error("Failed to load practice information:", error);
          toast.error(
            "Failed to load practice information. If this keeps happening, please email support@healthharbor.co with a screenshot of this page."
          );
          setLoadInfo(true);
        });
    }
  }, [open, practiceId, setValue, loadInfo]);

  const onSubmit = async (data: any) => {
    console.log("Form submission data:", data);
    console.log("Current practice index:", practiceIndex);
    console.log("Current provider index:", providerIndex);

    setIsSubmitting(true);
    try {
      if (isAddingPractice) {
        // Create new practice using dedicated route
        const practice = data.practices[practiceIndex];
        const practiceResponse = await baseApi.post("/dashboard/practices", {
          name: practice.name,
          tax_id: practice.tax_id,
          address: practice.address,
          city: practice.city,
          state: practice.state,
          zip: practice.zip,
          fax: practice.fax || null,
        }, { withCredentials: true });

        // Create provider for the new practice
        const provider = practice.providers[providerIndex];
        const newPracticeId = practiceResponse.data.practice?.id;
        if (newPracticeId) {
          await baseApi.post("/dashboard/providers", {
            practice_id: newPracticeId,
            name: provider.name,
            npi: provider.npi,
            medicaid_provider_number: provider.medicaid_provider_number || null,
          }, { withCredentials: true });
        }
        Mixpanel.track("New Practice Created");
        toast.success("Practice and provider added successfully!");
      } else {
        // Add provider to existing practice
        const practice = data.practices[practiceIndex];
        const provider = practice.providers[providerIndex];
        await baseApi.post("/dashboard/providers", {
          practice_id: practiceId,
          name: provider.name,
          npi: provider.npi,
          medicaid_provider_number: provider.medicaid_provider_number || null,
        }, { withCredentials: true });
        Mixpanel.track("New Provider Created");
        toast.success("Provider added successfully!");
      }

      refreshProfileInfo();
      onOpenChange(false);
      reset();
    } catch (error: any) {
      console.error("Submission error:", error);
      Mixpanel.track("Error: New Practice/Provider", { error: error });
      const message = error.response?.data?.detail || error.response?.data;
      toast.error(
        `${message || "Failed to add practice and provider"
        }. If this keeps happening, please email support@healthharbor.co with a screenshot of this page.`
      );
    }
    setIsSubmitting(false);
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  // Debug: Log current form values and errors
  useEffect(() => {
    if (loadInfo) {
      console.log("Current form values:", getValues());
      console.log("Current form errors:", errors);
      console.log("Form is valid:", isValid);
    }
  }, [errors, getValues, isValid, loadInfo]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Building className="h-5 w-5 text-[#005151]" />
            <span>
              {isAddingPractice
                ? "Add New Practice & Provider"
                : `Add New Provider to ${practiceName}`}
            </span>
          </DialogTitle>
          <DialogDescription>
            {isAddingPractice
              ? "Add basic information about your practice and provider."
              : "Add a new provider to the practice."}
          </DialogDescription>
        </DialogHeader>

        {!loadInfo ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#005151]"></div>
            <span className="ml-2 text-gray-600">Loading...</span>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onSubmit, (validationErrors) => {
              console.log("Practice form validation errors:", validationErrors);
              toast.error(
                "Some information is missing or invalid. Please correct the highlighted fields. If this keeps happening, email support@healthharbor.co with a screenshot of this page."
              );
            })}
            className="space-y-6"
          >
            {isAddingPractice && (
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">
                  Practice Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center">
                      Practice Name *
                      <span className="ml-1">
                        <Tooltip text={TOOLTIPS.PRACTICE_NAME} />
                      </span>
                    </label>
                    <Input
                      placeholder="Enter practice name"
                      {...register(`practices.${practiceIndex}.name` as const)}
                      onChange={(e) =>
                        handleInputChange(
                          `practices.${practiceIndex}.name`,
                          e.target.value
                        )
                      }
                      className={
                        getFieldError(`practices.${practiceIndex}.name`)
                          ? "border-red-500"
                          : ""
                      }
                    />
                    {getFieldError(`practices.${practiceIndex}.name`) && (
                      <p className="text-sm text-red-600">
                        {getFieldError(`practices.${practiceIndex}.name`)}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center">
                      Tax ID *
                      <span className="ml-1">
                        <Tooltip text={TOOLTIPS.PRACTICE_TAX_ID} />
                      </span>
                    </label>
                    <Input
                      placeholder="9 digits"
                      maxLength={9}
                      {...register(
                        `practices.${practiceIndex}.tax_id` as const
                      )}
                      onChange={(e) =>
                        handleInputChange(
                          `practices.${practiceIndex}.tax_id`,
                          e.target.value
                        )
                      }
                      className={
                        getFieldError(`practices.${practiceIndex}.tax_id`)
                          ? "border-red-500"
                          : ""
                      }
                    />
                    {getFieldError(`practices.${practiceIndex}.tax_id`) && (
                      <p className="text-sm text-red-600">
                        {getFieldError(`practices.${practiceIndex}.tax_id`)}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center">
                      Address *
                      <span className="ml-1">
                        <Tooltip text={TOOLTIPS.PRACTICE_ADDRESS} />
                      </span>
                    </label>
                    <Input
                      placeholder="Street address"
                      {...register(
                        `practices.${practiceIndex}.address` as const
                      )}
                      onChange={(e) =>
                        handleInputChange(
                          `practices.${practiceIndex}.address`,
                          e.target.value
                        )
                      }
                      className={
                        getFieldError(`practices.${practiceIndex}.address`)
                          ? "border-red-500"
                          : ""
                      }
                    />
                    {getFieldError(`practices.${practiceIndex}.address`) && (
                      <p className="text-sm text-red-600">
                        {getFieldError(`practices.${practiceIndex}.address`)}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      City *
                    </label>
                    <Input
                      placeholder="City"
                      {...register(`practices.${practiceIndex}.city` as const)}
                      onChange={(e) =>
                        handleInputChange(
                          `practices.${practiceIndex}.city`,
                          e.target.value
                        )
                      }
                      className={
                        getFieldError(`practices.${practiceIndex}.city`)
                          ? "border-red-500"
                          : ""
                      }
                    />
                    {getFieldError(`practices.${practiceIndex}.city`) && (
                      <p className="text-sm text-red-600">
                        {getFieldError(`practices.${practiceIndex}.city`)}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      State *
                    </label>
                    <Input
                      placeholder="State"
                      {...register(`practices.${practiceIndex}.state` as const)}
                      onChange={(e) =>
                        handleInputChange(
                          `practices.${practiceIndex}.state`,
                          e.target.value
                        )
                      }
                      className={
                        getFieldError(`practices.${practiceIndex}.state`)
                          ? "border-red-500"
                          : ""
                      }
                    />
                    {getFieldError(`practices.${practiceIndex}.state`) && (
                      <p className="text-sm text-red-600">
                        {getFieldError(`practices.${practiceIndex}.state`)}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Zip Code *
                    </label>
                    <Input
                      placeholder="5 or 9 digits"
                      {...register(`practices.${practiceIndex}.zip` as const)}
                      onChange={(e) =>
                        handleInputChange(
                          `practices.${practiceIndex}.zip`,
                          e.target.value
                        )
                      }
                      className={
                        getFieldError(`practices.${practiceIndex}.zip`)
                          ? "border-red-500"
                          : ""
                      }
                    />
                    {getFieldError(`practices.${practiceIndex}.zip`) && (
                      <p className="text-sm text-red-600">
                        {getFieldError(`practices.${practiceIndex}.zip`)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">
                Provider Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center">
                    Provider Name *
                    <span className="ml-1">
                      <Tooltip text={TOOLTIPS.BILLING_PROVIDER} />
                    </span>
                  </label>
                  <Input
                    placeholder="Provider's full name"
                    {...register(
                      `practices.${practiceIndex}.providers.${providerIndex}.name` as const
                    )}
                    onChange={(e) =>
                      handleInputChange(
                        `practices.${practiceIndex}.providers.${providerIndex}.name`,
                        e.target.value
                      )
                    }
                    className={
                      getFieldError(
                        `practices.${practiceIndex}.providers.${providerIndex}.name`
                      )
                        ? "border-red-500"
                        : ""
                    }
                  />
                  {getFieldError(
                    `practices.${practiceIndex}.providers.${providerIndex}.name`
                  ) && (
                      <p className="text-sm text-red-600">
                        {getFieldError(
                          `practices.${practiceIndex}.providers.${providerIndex}.name`
                        )}
                      </p>
                    )}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center">
                    NPI *
                    <span className="ml-1">
                      <Tooltip text={TOOLTIPS.NPI} />
                    </span>
                  </label>
                  <Input
                    placeholder="10 digits"
                    maxLength={10}
                    {...register(
                      `practices.${practiceIndex}.providers.${providerIndex}.npi` as const
                    )}
                    onChange={(e) =>
                      handleInputChange(
                        `practices.${practiceIndex}.providers.${providerIndex}.npi`,
                        e.target.value
                      )
                    }
                    className={
                      getFieldError(
                        `practices.${practiceIndex}.providers.${providerIndex}.npi`
                      )
                        ? "border-red-500"
                        : ""
                    }
                  />
                  {getFieldError(
                    `practices.${practiceIndex}.providers.${providerIndex}.npi`
                  ) && (
                      <p className="text-sm text-red-600">
                        {getFieldError(
                          `practices.${practiceIndex}.providers.${providerIndex}.npi`
                        )}
                      </p>
                    )}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-gradient-to-r from-[#005151] to-[#004040] hover:from-[#004040] hover:to-[#003030] text-white"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Adding...
                  </>
                ) : isAddingPractice ? (
                  "Add Practice & Provider"
                ) : (
                  "Add Provider"
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default NewPracticeDialog;
